import { UploadMonitorLegacy } from '../file-upload-legacy/upload-monitor-legacy';

/**
 * Classes and enums related to messages
 */
export interface ConversationLegacy {
  id: number;
  last_seen_message_id: number;
  messages: MessageLegacy[];
}

export enum MessageTypeLegacy {
  TEXT = 'text',
  IMAGE = 'image',
  FILE = 'file',
  BUTTON = 'button',
  VIDEO = 'video',
  LINK = 'link',
  PROGRESS_BAR = 'progress_bar',
  START_CONVERSATION = 'start-conversation',
  SEEN_CONVERSATION = 'seen-conversation',
  RESTART_CONVERSATION = 'restart-conversation',
  TEXT_INPUT = 'text_input',
  TEXT_INPUT_RESPONSE = 'text_input_response',
  POSTBACK = 'postback',
}

class TextLegacy {
  urls: URLMarkerLegacy[] = [];
  url_buttons?: UrlButtonLegacy[];
  parts?: { text: string; ariaHidden?: boolean }[];
  buttons?: TextButtonLegacy[];
  constructor(public content: string) {}
}

class URLMarkerLegacy {
  start: number;
  end: number;
}

export enum TextButtonTypeLegacy {
  URL_BUTTON = 'url_button',
}

interface TextButtonLegacy {
  type: TextButtonTypeLegacy;
  url_button?: UrlButtonLegacy;
}

interface UrlButtonLegacy {
  open_link_in_new_tab: boolean;
  title: string;
  web_url: string;

  // added by the front-end
  sanitizedUrl;
}

export class MediaLegacy {
  // Used when the backend returns messages to a GET
  width?: number;
  height?: number;
  url?: string; // built from media_uuid returned by the backend

  // Used when posting a new image message or to display a temporary
  constructor(public media_uuid?: string) {}
}

// We name this InMessageFile instead of File so that we don't mix up
// with existing javascript File class
export class InMessageFileLegacy {
  // Used when the backend returns messages to a GET
  mime_type: string;
  file_name: string;

  // Added by the frontend
  url?: string;

  // Used when posting a new file
  constructor(public uuid?: string) {}
}

export class ButtonLegacy {
  name: string;
  payload?: string;
}

export enum ButtonTypeLegacy {
  POSTBACK = 'postback',
  QUICK_REPLY = 'quick-reply',
}

export enum TextInputTypeLegacy {
  EMAIL = 'email',
  PHONE = 'phone',
}

export class TextInputLegacy {
  title: string;
  type: TextInputTypeLegacy;
  payload: string;
}

class TextInputResponseLegacy {
  type: TextInputTypeLegacy;
  content: string;
  payload: string;

  constructor(textInput: TextInputLegacy, content: string) {
    this.content = content;
    this.type = textInput.type;
    this.payload = textInput.payload;
  }
}

class PostBackLegacy {
  constructor(
    public title: string,
    public payload: string
  ) {}
}

class StartConversationLegacy {
  constructor(public ref: string) {}
}

// Note that messages parsed from the websocket are not really instances of this class
// (just js objects that conform to the structure) and hence don't have the 'new Date()' datetime
export class MessageLegacy {
  id: number;
  is_from_brand = false;
  datetime = new Date();
  type: MessageTypeLegacy;
  text?: TextLegacy;
  image?: MediaLegacy;
  file?: InMessageFileLegacy;
  button?: ButtonLegacy; // set when the user has clicked a button
  text_input?: TextInputLegacy; // special, not-displayed message which changes the type of the <input>
  text_input_response?: TextInputResponseLegacy;
  monitor?: UploadMonitorLegacy;
  buttons: ButtonLegacy[] = []; // set by the backend so that a set of buttons get displayed
  postFailed?: boolean; // an http error occurred while posting this message
  last_seen_message_id: number; // Last seen message from all messages except "seen_conversation" messages
  disable_input: boolean;
  buttonAlreadyClicked = false; // set by the UI, to prevent multiple clics on a message's buttons
  postback?: PostBackLegacy;
  // it would have been better to have snake case here to be consistent, but documentation has already been sent to client
  'start-conversation'?: StartConversationLegacy;

  static newTextMessage(content: string): MessageLegacy {
    const message = new MessageLegacy();
    message.type = MessageTypeLegacy.TEXT;
    message.text = new TextLegacy(content);
    return message;
  }

  static newImageMessage(mediaUUID: string): MessageLegacy {
    const message = new MessageLegacy();
    message.type = MessageTypeLegacy.IMAGE;
    message.image = new MediaLegacy(mediaUUID);
    return message;
  }

  static newFileMessage(fileUUID: string): MessageLegacy {
    const message = new MessageLegacy();
    message.type = MessageTypeLegacy.FILE;
    message.file = new InMessageFileLegacy(fileUUID);
    return message;
  }

  static newButtonMessage(button: ButtonLegacy): MessageLegacy {
    const message = new MessageLegacy();
    message.type = MessageTypeLegacy.BUTTON;
    message.button = button;
    return message;
  }

  static newUploadMonitorLegacy(monitor: UploadMonitorLegacy): MessageLegacy {
    const message = new MessageLegacy();
    message.type = MessageTypeLegacy.PROGRESS_BAR;
    message.monitor = monitor;
    return message;
  }

  static newTextInputResponseMessage(textInput, content) {
    const message = new MessageLegacy();
    message.type = MessageTypeLegacy.TEXT_INPUT_RESPONSE;
    message.text_input_response = new TextInputResponseLegacy(textInput, content);
    return message;
  }

  static newStartConversationMessage(ref?: string): MessageLegacy {
    const message = new MessageLegacy();
    message.type = MessageTypeLegacy.START_CONVERSATION;
    message['start-conversation'] = new StartConversationLegacy(ref);
    return message;
  }

  static newPostbackMessage(title, payload): MessageLegacy {
    const message = new MessageLegacy();
    message.type = MessageTypeLegacy.POSTBACK;
    message.postback = new PostBackLegacy(title, payload);
    return message;
  }

  static newSeenConversationMessage(last_seen_message_id: number): MessageLegacy {
    const message = new MessageLegacy();
    message.last_seen_message_id = last_seen_message_id;
    return message;
  }

  toBackendBody(): any {
    // Clean the body before sending it to the backend
    let body: any;
    if (this.isText()) {
      body = {
        [MessageTypeLegacy.TEXT]: {
          content: this[MessageTypeLegacy.TEXT].content,
        },
      };
    } else if (this.isImage()) {
      body = {
        [MessageTypeLegacy.IMAGE]: {
          media_uuid: this[MessageTypeLegacy.IMAGE].media_uuid,
        },
      };
    } else if (this.isFile()) {
      body = {
        [MessageTypeLegacy.FILE]: {
          uuid: this[MessageTypeLegacy.FILE].uuid,
        },
      };
    } else if (this.isButton()) {
      body = {
        [MessageTypeLegacy.BUTTON]: {
          name: this[MessageTypeLegacy.BUTTON].name,
          payload: this[MessageTypeLegacy.BUTTON].payload,
        },
      };
    } else if (this.isTextInputResponse()) {
      body = {
        [MessageTypeLegacy.TEXT_INPUT_RESPONSE]: {
          content: this[MessageTypeLegacy.TEXT_INPUT_RESPONSE].content,
          type: this[MessageTypeLegacy.TEXT_INPUT_RESPONSE].type,
          payload: this[MessageTypeLegacy.TEXT_INPUT_RESPONSE].payload,
        },
      };
    } else if (this.isStartConversation()) {
      body = {
        [MessageTypeLegacy.START_CONVERSATION]: {
          ref: this[MessageTypeLegacy.START_CONVERSATION].ref,
        },
      };
    } else if (this.isPostback()) {
      body = {
        [MessageTypeLegacy.POSTBACK]: {
          title: this[MessageTypeLegacy.POSTBACK].title,
          payload: this[MessageTypeLegacy.POSTBACK].payload,
        },
      };
    }
    body.type = this.type;

    return body;
  }

  // Be careful not to try and call these methods on message received from the backend:
  // as they are not created with a constructor, they won't have any methods of this class
  isText(): boolean {
    return this.type === MessageTypeLegacy.TEXT;
  }
  isImage(): boolean {
    return this.type === MessageTypeLegacy.IMAGE;
  }
  isFile(): boolean {
    return this.type === MessageTypeLegacy.FILE;
  }
  isButton(): boolean {
    return this.type === MessageTypeLegacy.BUTTON;
  }
  isProgressBar(): boolean {
    return this.type === MessageTypeLegacy.PROGRESS_BAR;
  }
  isStartConversation(): boolean {
    return this.type === MessageTypeLegacy.START_CONVERSATION;
  }
  isPostback(): boolean {
    return this.type === MessageTypeLegacy.POSTBACK;
  }
  isReStartConversation(): boolean {
    return this.type === MessageTypeLegacy.RESTART_CONVERSATION;
  }
  isTextInputResponse(): boolean {
    return this.type === MessageTypeLegacy.TEXT_INPUT_RESPONSE;
  }
}
